import React from "react";
import { ChevronDown, Layers, Settings2, Check, Star } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { isPresetModified } from "@/lib/hqPrefs";

/**
 * PresetSwitcher — compact selector at the top of the Right Now drawer.
 *
 * • Shows the active preset name (truncated on mobile).
 * • Dropdown lists every available preset with a check mark on the active one
 *   and a star on the default. Selecting a preset immediately applies its
 *   visibility + collapse layout via `onApply(preset)`.
 * • "Modified" chip surfaces when the caller has hand-toggled sections after
 *   selecting a preset. Two chip actions appear inline: Update preset (only
 *   for unlocked presets) and Save as new. Nothing is silently overwritten.
 * • Gear button opens the full manage dialog for CRUD.
 *
 * This component is presentation-only — all state lives one level up.
 */
export default function PresetSwitcher({
  presets = [],
  currentPresetId,
  defaultPresetId,
  hiddenSections = [],
  collapsedSections = [],
  onApply,
  onOpenManage,
  onUpdateCurrent,
  onSaveAsNew,
}) {
  const current = presets.find((p) => p.id === currentPresetId) || null;
  const modified = isPresetModified(current, hiddenSections, collapsedSections);

  return (
    <div className="mb-4" data-testid="rn-preset-switcher">
      <div className="flex items-center gap-1.5 min-w-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="rn-preset-trigger"
              className="flex-1 min-w-0 h-9 flex items-center gap-2 px-2.5 rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white/85 hover:text-white transition-colors"
              title={current?.description || "Right Now layouts"}
            >
              <Layers className="w-3.5 h-3.5 flex-shrink-0 text-white/60" />
              <span className="text-eyebrow flex-shrink-0">Layout</span>
              <span
                className="text-sm truncate flex-1 text-left"
                data-testid="rn-preset-current-name"
              >
                {current?.name || "Custom"}
              </span>
              <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-white/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="bg-[#151A23] border-white/10 text-white w-64"
            data-testid="rn-preset-menu"
          >
            <DropdownMenuLabel className="text-white/50 text-[11px] uppercase tracking-wider">
              Right Now layouts
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            {presets.length === 0 && (
              <div className="px-2 py-3 text-xs text-white/50">No presets yet.</div>
            )}
            {presets.map((p) => {
              const isActive = p.id === currentPresetId;
              const isDefault = p.id === defaultPresetId;
              return (
                <DropdownMenuItem
                  key={p.id}
                  onSelect={(e) => { e.preventDefault(); onApply && onApply(p); }}
                  className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                  data-testid={`rn-preset-item-${p.id}`}
                >
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? "opacity-100 text-emerald-400" : "opacity-0"}`} />
                    <span className="text-sm truncate flex-1">{p.name}</span>
                    {isDefault && (
                      <Star className="w-3 h-3 text-amber-300 flex-shrink-0" title="Default" />
                    )}
                    {p.is_locked && (
                      <span className="text-[9px] uppercase tracking-wider text-white/40">Reset</span>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); onOpenManage && onOpenManage(); }}
              className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
              data-testid="rn-preset-manage-item"
            >
              <div className="flex items-center gap-2 w-full">
                <Settings2 className="w-3.5 h-3.5 text-white/60" />
                <span className="text-sm">Manage presets…</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onOpenManage}
          data-testid="rn-preset-manage-btn"
          className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-md border border-white/10 bg-white/[0.04] text-white/50 hover:text-white hover:bg-white/[0.08] transition-colors"
          title="Manage presets"
          aria-label="Manage presets"
        >
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {modified && current && (
        <div
          className="mt-2 flex items-center gap-2 flex-wrap"
          data-testid="rn-preset-modified-row"
        >
          <span
            className="inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-amber-300/30 bg-amber-300/10 text-amber-200"
            data-testid="rn-preset-modified-chip"
          >
            Modified
          </span>
          {!current.is_locked && (
            <button
              type="button"
              onClick={onUpdateCurrent}
              data-testid="rn-preset-update-btn"
              className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors"
            >
              Update preset
            </button>
          )}
          <button
            type="button"
            onClick={onSaveAsNew}
            data-testid="rn-preset-save-as-btn"
            className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors"
          >
            Save as new
          </button>
        </div>
      )}
    </div>
  );
}
